import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] text-sm font-medium tracking-tight transition-[background-color,border-color,color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/15 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'cursor-pointer border border-transparent bg-primary text-primary-foreground shadow-[var(--shadow-soft)] hover:bg-primary/92',
        destructive:
          'cursor-pointer border border-transparent bg-destructive text-destructive-foreground shadow-[var(--shadow-soft)] hover:bg-destructive/92',
        outline: 'cursor-pointer border border-border bg-card/92 text-card-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'cursor-pointer border border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/86',
        ghost: 'cursor-pointer border border-transparent bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'h-auto cursor-pointer border border-transparent bg-transparent px-0 py-0 text-primary shadow-none hover:underline underline-offset-4',
        success:
          'cursor-pointer border border-transparent bg-[hsl(var(--status-success-solid))] text-white shadow-[var(--shadow-soft)] hover:brightness-95',
        warning:
          'cursor-pointer border border-transparent bg-warning text-warning-foreground shadow-[var(--shadow-soft)] hover:brightness-95',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3 text-xs',
        lg: 'h-11 px-8 text-sm',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  capitalize?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  ({ className, variant, size, asChild = false, capitalize = true, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    const buttonClass = cn(
      buttonVariants({ variant, size, className }),
      capitalize && 'capitalize',
      size === 'icon' && 'p-0 flex items-center justify-center'
    );

    return <Comp className={buttonClass} ref={ref} {...props} />;
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
